/**
 * **One list's state** — search, filter chips, sort and paging over rows the
 * page already holds (C14 §10.6 item 2; interface I-1).
 *
 *     const list = useListTable(jobs, {
 *       columns,                                   // ListColumn<ImportJob>[]
 *       search: ['filename'],                      // or (row) => [row.a, lookup(row.b)]
 *       filters: [{ key: 'status', label: 'Status', options, match: (r, v) => r.status === v }],
 *       initialSort: { field: 'created_at', dir: 'desc' },
 *     })
 *     <ListToolbar list={list} />  <ListTable list={list} rowKey={…} rowHref={…} />
 *
 * It sits **over the kit's `useTableFeatures`**, which owns sort and paging.
 * Search and filters happen here first, because the kit's search takes field
 * names only and a list is often searched by a name it looks up (a client's,
 * a service's). What it returns spreads straight onto the kit: `list.table`
 * onto `DataTable`, `list.toolbar` onto `TableToolbar`, `list.pagination`
 * onto `Pagination`. Server-side paging is not part of C14 (§10.6 item 8).
 */

import { type ColumnDef, type SortDir, useTableFeatures } from '@fiveox/ui'
import { useMemo, useState } from 'react'

/** A kit column, plus whether a phone shows it. With no column marked, a
 *  phone shows the first two and the one keyed `status` (§10.6 item 2). */
export interface ListColumn<T> extends ColumnDef<T> {
  phone?: boolean
}

/** One filter chip: a select of options, and how a row matches one. */
export interface ListFilter<T> {
  key: string
  /** What it filters — *Status*, *Kind*, *Owner*. */
  label: string
  /** The no-filter entry — *Any status*. Defaults to *All <label>*. */
  allLabel?: string
  options: Array<{ value: string; label: string }>
  match: (row: T, value: string) => boolean
  /** Where the chip starts. `''` (everything) by default. */
  initial?: string
}

/** Field names, or the text a row is found by. Every whitespace-separated
 *  term must appear somewhere, in any order — the kit's `useSearch` rule. */
export type ListSearch<T> = Array<keyof T> | ((row: T) => Array<string | number | null | undefined>)

export interface ListTableOptions<T> {
  columns: ListColumn<T>[]
  search?: ListSearch<T>
  filters?: ListFilter<T>[]
  initialSort?: { field: keyof T; dir?: SortDir }
  /** Rows per page to start with; the reader can change it. 25 by default. */
  pageSize?: number
}

export interface ListTableState<T> {
  columns: ListColumn<T>[]
  /** Every row the page holds, before search and filters. */
  all: T[]
  /** What search and filters leave, sorted — **what an export carries**. */
  sorted: T[]
  /** This page of `sorted`. */
  rows: T[]
  /** Nothing exists at all → the page's `EmptyState`. */
  isEmpty: boolean
  /** Rows exist and the search or a filter hides every one → `NoMatches`. */
  noMatches: boolean
  /** What is narrowing the list, in words — for `NoMatches`. */
  narrowedBy: string
  /** Spread onto the kit's `DataTable` (add `rowKey`). */
  table: {
    columns: ListColumn<T>[]
    rows: T[]
    sortField: keyof T | null
    sortDir: SortDir
    onSort: (field: keyof T) => void
  }
  /** Spread onto the kit's `TableToolbar`. No `setSearch` without `search`. */
  toolbar: {
    search?: string
    setSearch?: (value: string) => void
    shown: number
    total: number
    onReset: () => void
    canReset: boolean
  }
  /** Spread onto the kit's `Pagination`. */
  pagination: { page: number; totalPages: number; setPage: (page: number) => void }
  pageSize: number
  setPageSize: (size: number) => void
  /** The chips, each with its value and setter — `ListToolbar` draws them. */
  filters: Array<{ def: ListFilter<T>; value: string; set: (value: string) => void }>
  /** Clears the search and every chip. */
  reset: () => void
}

function initialValues<T>(filters: ListFilter<T>[]): Record<string, string> {
  return Object.fromEntries(filters.map((f) => [f.key, f.initial ?? '']))
}

export function useListTable<T extends object>(
  rows: T[],
  { columns, search, filters = [], initialSort, pageSize = 25 }: ListTableOptions<T>,
): ListTableState<T> {
  const [query, setQueryRaw] = useState('')
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(filters))

  const narrowed = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    return rows.filter((row) => {
      for (const f of filters) {
        const value = values[f.key] ?? ''
        if (value !== '' && !f.match(row, value)) return false
      }
      if (terms.length === 0 || !search) return true
      const parts =
        typeof search === 'function' ? search(row) : search.map((field) => row[field] as unknown)
      const haystack = parts
        .filter((part) => part !== null && part !== undefined)
        .map(String)
        .join(' ')
        .toLowerCase()
      return terms.every((term) => haystack.includes(term))
    })
    // `search` and `filters` are literals at every call site; their values
    // are what change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, values])

  // The kit sorts and pages; its own search is never used (see the header).
  const kit = useTableFeatures<T>({
    data: narrowed,
    searchFields: [],
    defaultSortField: initialSort?.field ?? null,
    defaultSortDir: initialSort?.dir ?? 'asc',
    pageSize,
  })

  const setQuery = (value: string) => {
    setQueryRaw(value)
    kit.setPage(1)
  }
  const setFilter = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    kit.setPage(1)
  }
  const reset = () => {
    setQueryRaw('')
    setValues(Object.fromEntries(filters.map((f) => [f.key, ''])))
    kit.setPage(1)
  }

  const activeChips = filters.filter((f) => (values[f.key] ?? '') !== '')
  const narrowedBy = [
    query.trim(),
    ...activeChips.map(
      (f) => `${f.label}: ${f.options.find((o) => o.value === values[f.key])?.label ?? values[f.key]}`,
    ),
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    columns,
    all: rows,
    sorted: kit.sorted,
    rows: kit.paged,
    isEmpty: rows.length === 0,
    noMatches: rows.length > 0 && narrowed.length === 0,
    narrowedBy,
    table: {
      columns,
      rows: kit.paged,
      sortField: kit.sortField,
      sortDir: kit.sortDir,
      onSort: kit.handleSort,
    },
    toolbar: {
      ...(search ? { search: query, setSearch: setQuery } : {}),
      shown: narrowed.length,
      total: rows.length,
      onReset: reset,
      canReset: query !== '' || activeChips.length > 0,
    },
    pagination: { page: kit.page, totalPages: kit.totalPages, setPage: kit.setPage },
    pageSize: kit.pageSize,
    setPageSize: kit.setPageSize,
    filters: filters.map((def) => ({
      def,
      value: values[def.key] ?? '',
      set: (value: string) => setFilter(def.key, value),
    })),
    reset,
  }
}
